<template>
  <!-- 订单列表字段配置模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              列表字段配置
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              自定义订单列表的字段显示和排序
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- 配置说明和操作按钮 -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 bg-blue-50 rounded-lg hidden sm:flex">
          <div class="flex-1">
            <div class="text-sm text-blue-800 space-y-1">
              <div>• 拖拽调整字段显示顺序</div>
              <div>• 开关控制字段显示/隐藏</div>
              <div>• 设置列宽优化显示效果</div>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 ml-6">
            <button
              @click="resetConfig"
              :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              重置配置
            </button>
            <button
              @click="saveConfig"
              :disabled="!hasChanges || submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {{ submitting ? '保存中...' : '保存配置' }}
            </button>
          </div>
        </div>

        <!-- 字段配置列表 -->
        <div class="space-y-3">
          <!-- 表格头部 (桌面端) -->
          <div class="hidden sm:grid grid-cols-10 gap-4 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">类型</div>
            <div class="col-span-2 text-center">列宽</div>
            <div class="col-span-2 text-center">显示</div>
          </div>

          <!-- 移动端表格头部 -->
          <div class="grid grid-cols-6 gap-2 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200 sm:hidden">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">显示</div>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-12">
            <!-- Loading Spinner -->
            <div class="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
            <div class="text-sm text-gray-600">加载中...</div>
          </div>

          <!-- 字段配置项 -->
          <draggable
            v-else
            v-model="localFieldConfigs"
            group="fieldConfigs"
            @end="handleSort"
            item-key="id"
            class="space-y-2"
          >
            <template #item="{ element: config }">
              <div>
                <!-- 桌面端布局 -->
                <div class="hidden sm:grid grid-cols-10 gap-4 items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center cursor-move">
                    <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                    </svg>
                  </div>

                  <!-- 字段名称 -->
                  <div class="col-span-3 text-sm font-medium text-gray-900">
                    {{ config.field_label }}
                  </div>

                  <!-- 字段类型 -->
                  <div class="col-span-2 text-center">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      系统字段
                    </span>
                  </div>

                  <!-- 列宽 -->
                  <div class="col-span-2 flex justify-center">
                    <input
                      type="number"
                      v-model.number="config.column_width"
                      min="80"
                      max="500"
                      class="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-center"
                      @input="handleChange"
                    />
                  </div>

                  <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        v-model="config.is_visible"
                        @change="handleChange"
                        class="sr-only peer"
                      >
                      <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <!-- 移动端布局 -->
                <div class="grid grid-cols-6 gap-2 items-center p-3 bg-gray-50 rounded-lg sm:hidden">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center cursor-move">
                    <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                    </svg>
                  </div>

                  <!-- 字段名称 -->
                  <div class="col-span-3 text-sm font-medium text-gray-900">
                    {{ config.field_label }}
                  </div>

                  <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        v-model="config.is_visible"
                        @change="handleChange"
                        class="sr-only peer"
                      >
                      <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <!-- 移动端额外信息（展开显示列宽设置） -->
                  <div class="col-span-6 mt-2 flex items-center space-x-2">
                    <span class="text-xs text-gray-500">列宽:</span>
                    <input
                      type="number"
                      v-model.number="config.column_width"
                      min="80"
                      max="500"
                      class="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      @input="handleChange"
                    />
                    <span class="text-xs text-gray-500">px</span>
                  </div>
                </div>
          </div>
            </template>
          </draggable>
        </div>

        <!-- 移动端底部操作按钮 -->
        <div class="mt-6 flex justify-end space-x-3 sm:hidden">
          <button
            @click="resetConfig"
            :disabled="submitting"
            class="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            重置
          </button>
          <button
            @click="saveConfig"
            :disabled="!hasChanges || submitting"
            class="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import draggable from 'vuedraggable'
import orderService from '@/services/orderService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()
const loading = ref(false)
const submitting = ref(false)
const localFieldConfigs = ref([])
const originalFieldConfigs = ref([])
const hasChanges = ref(false)

// 监听配置变化
const handleChange = () => {
  hasChanges.value = true
}

// 监听拖拽排序
const handleSort = () => {
  // 更新排序
  localFieldConfigs.value.forEach((config, index) => {
    config.sort_order = index
  })
  hasChanges.value = true
}

watch(() => props.show, async (newShow) => {
  if (newShow) {
    await loadConfigs()
  }
})

const loadConfigs = async () => {
  loading.value = true
  try {
    const result = await orderService.getFieldConfigs()
    if (result.success && Array.isArray(result.data)) {
      localFieldConfigs.value = JSON.parse(JSON.stringify(result.data))
      originalFieldConfigs.value = JSON.parse(JSON.stringify(result.data))
      hasChanges.value = false
    } else {
      localFieldConfigs.value = []
      originalFieldConfigs.value = []
    }
  } catch (error) {
    console.error('加载字段配置失败:', error)
    toast.error('加载字段配置失败')
    localFieldConfigs.value = []
    originalFieldConfigs.value = []
  } finally {
    loading.value = false
  }
}

const saveConfig = async () => {
  submitting.value = true
  try {
    const result = await orderService.batchUpdateFieldConfigs(localFieldConfigs.value)
    if (result.success) {
      toast.success('字段配置保存成功')
      hasChanges.value = false
      emit('success')
      emit('close')
    } else {
      toast.error(result.message || '保存字段配置失败')
    }
  } catch (error) {
    console.error('保存字段配置失败:', error)
    toast.error('保存字段配置失败')
  } finally {
    submitting.value = false
  }
}

const resetConfig = async () => {
  if (!confirm('确定要重置所有字段配置吗？这将恢复默认设置。')) {
    return
  }
  
  submitting.value = true
  try {
    const result = await orderService.resetFieldConfigs()
    if (result.success) {
      toast.success('字段配置已重置')
      await loadConfigs()
      emit('success')
    } else {
      toast.error(result.message || '重置字段配置失败')
    }
  } catch (error) {
    console.error('重置字段配置失败:', error)
    toast.error('重置字段配置失败')
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  if (hasChanges.value) {
    if (!confirm('您有未保存的更改，确定要关闭吗？')) {
      return
    }
  }
  emit('close')
}
</script>
